import * as React from 'react'

import { cn } from '../lib/utils'
import { Input } from './input'
import { Tooltip } from './tooltip'

interface TimePickerProps {
  className?: string
  time: string | undefined
  onSelect: (time: string) => void
  disabled?: boolean
  label?: string
  name?: string
  tooltipContent?: string
}

function convertTo12Hour(time24: string): string {
  if (!time24) return ''

  const [hours, minutes] = time24.split(':')
  const hour = parseInt(hours)

  if (hour === 0) {
    return `12:${minutes} AM`
  } else if (hour === 12) {
    return `12:${minutes} PM`
  } else if (hour > 12) {
    return `${hour - 12}:${minutes} PM`
  } else {
    return `${hour}:${minutes} AM`
  }
}

function convertTo24Hour(time12: string | undefined): string {
  if (!time12) return ''

  const match = time12.match(/^(\d{1,2}):(\d{2}) (AM|PM)$/)
  if (!match) return time12

  const [, hours, minutes, period] = match
  let hour = parseInt(hours)

  if (period === 'PM' && hour !== 12) {
    hour += 12
  } else if (period === 'AM' && hour === 12) {
    hour = 0
  }

  return `${hour.toString().padStart(2, '0')}:${minutes}`
}

export function TimePicker({ time, onSelect, disabled, label, name, tooltipContent, className }: TimePickerProps) {
  const [inputValue, setInputValue] = React.useState(convertTo24Hour(time))
  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    setInputValue(convertTo24Hour(time))
  }, [time])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const timeValueIn24HourFormat = e.target.value
    setInputValue(timeValueIn24HourFormat)

    if (timeValueIn24HourFormat) {
      const timeValueIn12HourFormat = convertTo12Hour(timeValueIn24HourFormat)
      onSelect(timeValueIn12HourFormat)
    }
  }

  const handleClick = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.showPicker()
    }
  }

  return (
    <Tooltip content={tooltipContent || 'Select time'} hidden={disabled}>
      <div className={cn('w-[140px]')} onClick={handleClick}>
        <Input
          ref={inputRef}
          type="time"
          value={inputValue}
          onChange={handleChange}
          disabled={disabled}
          name={name}
          className={cn('w-full cursor-pointer pr-2', className)}
          aria-label={label || 'Select time'}
          style={{ colorScheme: 'normal' }}
        />
      </div>
    </Tooltip>
  )
}
